import React from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink, MapPin, Quote, Phone } from "lucide-react";
import SEO from "../components/SEO";
import { PHONE, PHONE_TEL, BRAND, GOOGLE_REVIEW_URL } from "../data/content";
import { reviews, SUMMARY } from "../data/reviews";

export default function Reviews() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: BRAND,
    telephone: "+611800836390",
    url: "https://techemergency.com.au",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: SUMMARY.rating,
      reviewCount: SUMMARY.total,
      bestRating: "5",
      worstRating: "1",
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: "5" },
      author: { "@type": "Person", name: r.name },
      reviewBody: r.text,
      ...(r.date ? { datePublished: r.date } : {}),
    })),
  };

  return (
    <>
      <SEO
        title={`${SUMMARY.rating}★ Google Reviews — ${SUMMARY.total}+ Adelaide Families & Businesses | ${BRAND}`}
        description={`Read real Google reviews of Tech Emergency from Adelaide families and businesses across Glenelg, Norwood, Mawson Lakes, Tea Tree Gully and more. Rated ${SUMMARY.rating}★ from ${SUMMARY.total}+ reviews.`}
        path="/reviews"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="pt-20 pb-16 md:pb-20 relative overflow-hidden">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{ background: "var(--te-primary)" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="te-overline mb-4">/ Reviews</div>

          {/* Big rating headline */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tighter">
                <span style={{ color: "var(--te-primary)" }}>{SUMMARY.rating}★</span> on Google,
                <br />
                from {SUMMARY.total}+ real<br />
                Adelaide customers.
              </h1>
            </div>
          </div>

          <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl mb-8">
            Every single one of these reviews is real, written voluntarily by a customer
            we worked with, and verified on Google. No incentives, no edits — we just do
            the job well and our customers do the talking.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={SUMMARY.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-page-read-all"
              className="te-btn te-btn-primary"
            >
              <Star size={16} fill="currentColor" stroke="none" />
              Read all {SUMMARY.total}+ on Google
              <ExternalLink size={14} />
            </a>
            <Link
              to="/review"
              data-testid="reviews-page-leave-one"
              className="te-btn te-btn-ghost"
            >
              Leave us a review →
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEWS GRID */}
      <section className="pb-24" data-testid="reviews-grid">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-6">/ Featured Google reviews</div>
          <div className="grid md:grid-cols-2 gap-5">
            {reviews.map((r, i) => (
              <article
                key={i}
                className="te-card p-7 md:p-8 relative flex flex-col"
                data-testid={`review-card-${i}`}
              >
                <Quote
                  size={32}
                  className="absolute top-6 right-6 opacity-10"
                  style={{ color: "var(--te-primary)" }}
                  aria-hidden="true"
                />

                {/* Stars */}
                <div className="flex gap-1 mb-5" aria-label={`${r.rating} out of 5 stars`}>
                  {[...Array(5)].map((_, idx) => (
                    <Star
                      key={idx}
                      size={16}
                      fill={idx < r.rating ? "var(--te-primary)" : "transparent"}
                      stroke="var(--te-primary)"
                    />
                  ))}
                </div>

                {/* Body */}
                <blockquote className="text-zinc-200 leading-relaxed mb-6 flex-1 text-base sm:text-lg">
                  "{r.text}"
                </blockquote>

                {/* Attribution */}
                <div className="flex items-center gap-3 pt-5 border-t border-white/10">
                  <div
                    className="w-10 h-10 flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
                    style={{ background: "var(--te-primary)" }}
                    aria-hidden="true"
                  >
                    {r.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-zinc-100">{r.name}</div>
                    <div className="te-overline text-[10px] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                      {r.suburb && (
                        <span className="flex items-center gap-1">
                          <MapPin size={11} style={{ color: "var(--te-primary)" }} />
                          {r.suburb}
                        </span>
                      )}
                      {r.tech && <span>TECH: {r.tech}</span>}
                      <span>VIA GOOGLE</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* See more on Google */}
          <div className="mt-12 te-card p-7 md:p-9 flex flex-col md:flex-row md:items-center gap-6 text-center md:text-left">
            <Star
              size={42}
              fill="var(--te-primary)"
              stroke="none"
              className="mx-auto md:mx-0 flex-shrink-0"
            />
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-2">
                Want to see the other {Math.max(0, SUMMARY.total - reviews.length)}+ reviews?
              </h2>
              <p className="text-zinc-400 leading-relaxed">
                We've featured a handful above. The complete library lives on Google —
                tap below to read every review we've ever received.
              </p>
            </div>
            <a
              href={SUMMARY.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-page-bottom-cta"
              className="te-btn te-btn-primary shrink-0"
            >
              Read all on Google <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* LEAVE A REVIEW CTA */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-card p-8 md:p-12 text-center relative overflow-hidden">
            <div
              className="absolute -top-32 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
              style={{ background: "var(--te-primary)" }}
            />
            <div className="relative">
              <div className="te-overline mb-3">/ Already a customer?</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-4">
                30 seconds. Massive impact.
              </h2>
              <p className="text-zinc-300 max-w-xl mx-auto leading-relaxed mb-8">
                If we did right by you, a quick Google review honestly helps a small
                Adelaide business more than you'd believe. Takes about 30 seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reviews-page-write-btn"
                  className="te-btn te-btn-primary"
                >
                  <Star size={16} fill="currentColor" stroke="none" />
                  Write a Google review
                </a>
                <a
                  href={PHONE_TEL}
                  data-testid="reviews-page-call-btn"
                  className="te-btn te-btn-ghost"
                >
                  <Phone size={16} /> {PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
